import { useCallback, useEffect, useState } from "react";

export type HistoryType = "work" | "star" | "news";

export interface HistoryItem {
  type: HistoryType;
  id: string;
  title: string;
  subtitle: string;
  cover: string;
  path: string;
  viewedAt: number;
}

const STORAGE_KEY = "ztg_browse_history";
const MAX_ITEMS = 10;

export function readHistory(): HistoryItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as HistoryItem[]) : [];
  } catch {
    return [];
  }
}

export function addHistory(item: Omit<HistoryItem, "viewedAt">) {
  try {
    const current = readHistory().filter((h) => !(h.type === item.type && h.id === item.id));
    const next: HistoryItem[] = [{ ...item, viewedAt: Date.now() }, ...current].slice(0, MAX_ITEMS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event("ztg-history-updated"));
  } catch {
    /* ignore storage errors */
  }
}

export function clearHistory() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new Event("ztg-history-updated"));
  } catch {
    /* ignore */
  }
}

export function useBrowseHistory() {
  const [history, setHistory] = useState<HistoryItem[]>([]);

  useEffect(() => {
    const sync = () => setHistory(readHistory());
    sync();
    window.addEventListener("ztg-history-updated", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("ztg-history-updated", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const clear = useCallback(() => clearHistory(), []);

  return { history, clear };
}